// Updated Contact.js with mobile-friendly design
import React, { useState, useEffect, useRef } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const contactRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const currentRef = contactRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: 'e0dbb4a6-ef5c-4079-9b0c-bcd5c6def6f8',
          to_email: 'admin@gamingrid.co',
          from_name: formData.name,
          email: formData.email,
          subject: `New Contact from ${formData.name} - ${formData.company || 'GamingGrid Website'}`,
          message: formData.message,
          company: formData.company
        })
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);
        // Reset form after success
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            company: '',
            message: ''
          });
          setIsSubmitted(false);
        }, 3000);
      } else {
        alert('Failed to send message. Please try again or email us directly at admin@gamingrid.co');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Failed to send message. Please try again or email us directly at admin@gamingrid.co');
    }

    setIsSubmitting(false);
  };

  return (
    <section
      id="contact"
      className={`contact ${isVisible ? 'visible' : ''}`}
      ref={contactRef}
    >
      <div className="container">
        <div className="section-header-experimental">
          <div className="header-decoration">
            <div className="decoration-line"></div>
            <h2>Get In Touch</h2>
            <div className="decoration-line"></div>
          </div>
          <p>Ready to level up your game's marketing? Let's start the conversation.</p>
        </div>

        <div className="contact-content">
          <div className="contact-info">
            <h3>Let's Build Something Amazing</h3>
            <p className="contact-description">
              Whether you're launching a new game, growing your community, or building an esports ecosystem, we're here to help you succeed.
            </p>

            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12.713l-11.985-9.713h23.97l-11.985 9.713zm0 2.574l-12-9.725v15.438h24v-15.438l-12 9.725z" />
                  </svg>
                </div>
                <div className="contact-text">
                  <h4>Email Us</h4>
                  <p>admin@gaminggrid.co</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                  </svg>
                </div>
                <div className="contact-text">
                  <h4>Based In</h4>
                  <p>Global</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </div>
                <div className="contact-text">
                  <h4>Follow Us</h4>
                  <p>@GamingGridX</p>
                </div>
              </div>
            </div>

            <div className="contact-pattern">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="pattern-dot"></div>
              ))}
            </div>
          </div>

          <div className="contact-form-container">
            {isSubmitted ? (
              <div className="success-state">
                <div className="success-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </div>
                <h3>Message Sent!</h3>
                <p>We'll get back to you within 24 hours. Let's build something amazing together!</p>
                <div className="success-particles">
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className="success-particle"></div>
                  ))}
                </div>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <div className="input-container">
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder=" "
                    />
                    <label htmlFor="name" className="form-label">Your Name *</label>
                    <div className="input-border"></div>
                  </div>
                </div>

                <div className="form-group">
                  <div className="input-container">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder=" "
                    />
                    <label htmlFor="email" className="form-label">Email Address *</label>
                    <div className="input-border"></div>
                  </div>
                </div>

                <div className="form-group">
                  <div className="input-container">
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="form-input"
                      placeholder=" "
                    />
                    <label htmlFor="company" className="form-label">Company/Studio</label>
                    <div className="input-border"></div>
                  </div>
                </div>

                <div className="form-group">
                  <div className="input-container textarea-container">
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="4"
                      className="form-input"
                      placeholder=" "
                    ></textarea>
                    <label htmlFor="message" className="form-label">Your Message *</label>
                    <div className="input-border"></div>
                  </div>
                </div>

                <button
                  type="submit"
                  className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="brand-loading-spinner"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                      </svg>
                    </>
                  )}
                </button>

                <div className="form-particles">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="form-particle"></div>
                  ))}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;